import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, alpha } from '@mui/material';
import { ChartSkeleton } from './skeletons';
import { phaseColor, readinessColor } from './helpers';

// ═══════════════════════════════════════════════════════════════════════════

const SummaryCardsBlock = ({ isFirstLoad, data, summary, chartData, t, T }) => (
      isFirstLoad ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 4fr 3fr' }, gap: 2, mb: 2.5 }}>
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </Box>
      ) : data && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 4fr 3fr' }, gap: 2, mb: 2.5, alignItems: 'stretch' }}>

          {/* ① Signal Distribution — horizontal bars */}
          <Box sx={{
            bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0',
            p: 2.5, display: 'flex', flexDirection: 'column',
          }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', mb: 2 }}>
              {T('chart.signalDistribution')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, justifyContent: 'center' }}>
              {chartData.map((item) => {
                const total = chartData.reduce((s, d) => s + d.value, 0) || 1;
                const pct = ((item.value / total) * 100).toFixed(0);
                return (
                  <Box key={item.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569' }}>{item.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: item.color }}>{item.value} <span style={{ fontWeight: 500, color: '#94a3b8' }}>({pct}%)</span></Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: alpha(item.color, 0.1), borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: item.color, borderRadius: 1, transition: 'width 0.6s ease' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* ② Wyckoff Phase Distribution */}
          {summary.wyckoff ? (
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', p: 2.5, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', mb: 2 }}>
                {T('chart.wyckoffAnalysis')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, justifyContent: 'center' }}>
                {Object.entries(summary.wyckoff.phase_distribution || {})
                  .filter(([phase]) => ['accumulation', 'markup', 'distribution', 'absorption'].includes(phase))
                  .sort(([, a], [, b]) => b - a)
                  .map(([phase, count]) => {
                    const total = Object.entries(summary.wyckoff.phase_distribution || {})
                      .filter(([p]) => ['accumulation', 'markup', 'distribution', 'absorption'].includes(p))
                      .reduce((s, [, v]) => s + v, 0) || 1;
                    const pct = ((count / total) * 100).toFixed(0);
                    return (
                      <Box key={phase} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                          background: `linear-gradient(135deg, ${phaseColor(phase)} 0%, ${alpha(phaseColor(phase), 0.7)} 100%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: `0 2px 6px ${alpha(phaseColor(phase), 0.25)}`,
                        }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{count}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: phaseColor(phase) }}>
                              {t(`phase.phases.${phase}`, phase)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#94a3b8' }}>{pct}%</Typography>
                          </Box>
                          <Box sx={{ height: 5, bgcolor: alpha(phaseColor(phase), 0.1), borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: phaseColor(phase), borderRadius: 1, transition: 'width 0.6s ease' }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          ) : <Box />}

          {/* ③ Readiness Overview */}
          {summary.wyckoff ? (() => {
            const readiness = summary.wyckoff.avg_markup_readiness || 0;
            const pct = (readiness * 100).toFixed(0);
            const rc = readinessColor(readiness);
            const breakout = summary.wyckoff.sap_but_pha || 0;
            const preparing = summary.wyckoff.chuan_bi || 0;
            return (
            <Box sx={{
              borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              background: `linear-gradient(180deg, ${alpha(rc, 0.04)} 0%, #fff 50%)`,
            }}>
              {/* Top — gauge area */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 2.5, pb: 1.5, px: 2 }}>
                <Box sx={{ position: 'relative', width: 80, height: 80, mb: 1 }}>
                  <svg width="80" height="80" viewBox="0 0 80 80" style={{ display: 'block' }}>
                    <circle cx="40" cy="40" r="34" fill="none" stroke={alpha(rc, 0.12)} strokeWidth="5" />
                    <circle cx="40" cy="40" r="34" fill="none"
                      stroke={rc} strokeWidth="5" strokeLinecap="round"
                      strokeDasharray={`${readiness * 213.63} 213.63`}
                      transform="rotate(-90 40 40)"
                      style={{ transition: 'stroke-dasharray 0.8s ease', filter: `drop-shadow(0 0 4px ${alpha(rc, 0.4)})` }}
                    />
                  </svg>
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: rc, lineHeight: 1, fontFamily: 'monospace' }}>
                      {pct}%
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>
                  {T('chart.avgReadiness')}
                </Typography>
              </Box>
              {/* Bottom — two stats side by side */}
              <Box sx={{ display: 'flex', borderTop: '1px solid #f1f5f9' }}>
                <Box sx={{
                  flex: 1, py: 1.2, textAlign: 'center',
                  borderRight: '1px solid #f1f5f9',
                  transition: 'background 0.2s',
                  '&:hover': { bgcolor: alpha('#10b981', 0.04) },
                }}>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#10b981', lineHeight: 1, fontFamily: 'monospace' }}>
                    {breakout}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600, mt: 0.3 }}>
                    {T('chart.aboutToBreakout')}
                  </Typography>
                </Box>
                <Box sx={{
                  flex: 1, py: 1.2, textAlign: 'center',
                  transition: 'background 0.2s',
                  '&:hover': { bgcolor: alpha('#f59e0b', 0.04) },
                }}>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#f59e0b', lineHeight: 1, fontFamily: 'monospace' }}>
                    {preparing}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600, mt: 0.3 }}>
                    {T('chart.preparing')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            );
          })() : <Box />}

        </Box>
      )
);
SummaryCardsBlock.propTypes = {
  isFirstLoad: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  summary: PropTypes.object,
  chartData: PropTypes.array,
  t: PropTypes.func,
  T: PropTypes.func,
};

// ═══════════════════════════════════════════════════════════════════════════
// TickerDetailDrawerBlock (extracted for cognitive complexity)
// ═══════════════════════════════════════════════════════════════════════════

export default SummaryCardsBlock;
